import { Logger } from '~~/helpers/logger';
import type {
  Address,
  Payment,
  CartUpdateAction,
  LineItem,
  Customer,
  Cart,
  ProductVariant,
  CustomQuery,
  CustomFieldInput,
} from '~~/types/commercetools';
import { CartState, TaxMode } from '~~/types/commercetools';
import { userGetters, useCart, useUser } from '~/utils/ct';
import {
  setCustomerEmail,
  setShippingAddressAction,
  setBillingAddressAction,
  addPayment,
  addExtraGuaranteeLineItem,
  setCustomTypeAction,
  setCustomFieldsAction,
} from '~~/helpers/cart/cartActions';
import { CUSTOM_QUERIES } from '~~/constants/customQueries';

import type { VatNumber } from '~~/helpers/cart/customFields';
import { getSelfDeclaration, setAddressCustomFieldsAction } from '~~/helpers/cart/customFields';
import { ADDRESS_TYPE } from '~~/types/AddressType';
import { CartUpdateAction as CiaUpdateAction } from '~/composables/useCia/event/tracking/cart-update';
import type { VatValidationResult } from '~~/types/vatValidation';
import callFunctionWithRetries from '~~/helpers/retry/callFunctionWithRetries';
import { getStoreKey } from '~~/helpers/cart/getStoreKey';
import { isForbiddenCartUpdateAction } from '~~/helpers/cart/isForbiddenCartUpdateAction';
import { asyncDebounce } from '~~/helpers/lodash/asyncDebounce';
import { DEBOUNCE_TIMES } from '~~/constants/debounceTimes';
import type { EventSourceLocation } from '~~/types/integrations/tagManager/TagManagerIntegration';
import { CART_PROMISE_QUEUE_KEY } from '~~/constants/checkout';
import { getIsExtraGuaranteeOnlyCart } from '~~/helpers/cart/getIsExtraGuaranteeOnlyCart';

export const ERROR_NOT_FOUND_IN_STORE = 'was not found in store';

export default function () {

  const customError = useState('useCartExtended-customError', () => ({ updateCustomCart: '' }));

  const {
    cart,
    setCart: oldSetCart,
    load: oldLoad,
    replicateCart: oldReplicateCart,
    addItem: oldAddItem,
    removeItem: oldRemoveItem,
    updateItemQty: oldUpdateItemQty,
    clear: oldClear,
    applyCoupon: oldApplyCoupon,
    removeCoupon: oldRemoveCoupon,
    updateCart: oldUpdateCart,
    loading,
    error: oldError,
  } = useCart();

  const {
    loadStock,
  } = useStock('cart');

  const { utmSourceCookie } = useCookies();

  const isLoaded = ref(false);

  const setCart = oldSetCart as (cart: Cart | null) => void;

  const UPDATE_CART_QUERY = CUSTOM_QUERIES.UPDATE_CART_CUSTOM;

  const { countryLocalization } = useLocalization();
  const { clientType: billingClientType } = useCheckoutClientType(ADDRESS_TYPE.Billing);
  const { event: ciaEvent } = useCia();

  const oldAddItemQueued = usePromiseQueue(oldAddItem, CART_PROMISE_QUEUE_KEY).execute;
  const oldRemoveItemQueued = usePromiseQueue(oldRemoveItem, CART_PROMISE_QUEUE_KEY).execute;
  const oldUpdateItemQtyQueued = usePromiseQueue(oldUpdateItemQty, CART_PROMISE_QUEUE_KEY).execute;
  const oldReplicateCartQueued = usePromiseQueue(oldReplicateCart, CART_PROMISE_QUEUE_KEY).execute;
  const oldClearQueued = usePromiseQueue(oldClear, CART_PROMISE_QUEUE_KEY).execute;
  const oldApplyCouponQueued = usePromiseQueue(oldApplyCoupon, CART_PROMISE_QUEUE_KEY).execute;
  const oldRemoveCouponQueued = usePromiseQueue(oldRemoveCoupon, CART_PROMISE_QUEUE_KEY).execute;
  const oldUpdateCartQueued = usePromiseQueue(oldUpdateCart, CART_PROMISE_QUEUE_KEY).execute;

  const reloadCart = async () => {

    setCart(null);
    await load();

  };

  const error = computed(() => ({
    ...oldError.value,
    ...customError.value,
  }));

  const { user } = useUser();
  const { setCartId, setEmail, ciaState } = useCiaState();
  const loggedCreatedCartId = ref<string | null>(null);

  // TODO - revert syntax once fix is pushed for type incoherence between composables and getters
  // https://expondo.atlassian.net/browse/INSP-988
  const userEmailAddress = computed(() => userGetters.getEmailAddress(user.value as Customer));
  const emailAddress = computed(() => cart.value?.customerEmail);

  const isCartActive = computed(() => cart.value?.cartState === CartState.Active);
  const isCartFrozen = computed(() => cart.value?.cartState === CartState.Frozen);

  const handleCartUpdate = ({ action, sku }:
    { action: CiaUpdateAction, sku: string }) => {

    try {

      ciaEvent.cartUpdate({
        action,
        sku,
        cartId: ciaState.value.cartId,
        email: ciaState.value.email,
        userId: ciaState.value.userId,
      });

    } catch (error) {

      Logger.error(`cia|cartUpdate error: ${error}`);

    }

  };

  const persistCartId = (cartId?: string) => {

    if (cartId) {

      try {

        setCartId(cartId);

      } catch (error) {

        Logger.error(`cia|setCartId error: ${error}`);

      }

    }

  };

  const logCreatedCartId = (cartId: Maybe<string>) => {

    if (cartId && loggedCreatedCartId.value !== cartId) {

      Logger.info(
        `[CT Cart Created] id=${cartId}, version=${cart.value?.version}, createdAt=${cart.value?.createdAt},' +
        ' store=${cart.value?.store}, customerId=${cart.value?.customerId}`,
      );
      loggedCreatedCartId.value = cartId;

    }

  };

  function getUpdateCustomerEmailAction(email: Maybe<string>): CartUpdateAction | undefined {

    if (!email) {

      return;

    }

    if (emailAddress.value === email) {

      return;

    }
    try {

      setEmail(email);

    } catch (error) {

      Logger.error(`cia|setEmail error: ${error}`);

    }

    return setCustomerEmail(email);

  }

  async function updateCustomerEmail(email: Maybe<string>, triggeredIn?: string) {

    try {

      if (!cart.value) {

        await load();

      }

      if (!cart.value) {

        Logger.debug(`[updateCustomerEmail] Omitting setting customer email on cart as the cart does not exist.
          Triggered in: ${triggeredIn}`);

        return;

      }
      const action = getUpdateCustomerEmailAction(email);

      if (action) {

        await updateCustomCart([action]);

      }

    } catch (error) {

      Logger.error(`[updateCustomerEmail] Error updating customer email: ${error}. Triggered in: ${triggeredIn}`);

    }

  }

  async function updatePayment(id: Payment['id']) {

    if (cart.value) {

      const actions: CartUpdateAction[] = [addPayment({ id })];
      await updateCustomCart(actions);

    }

  }

  async function addExtraGuaranteeProduct(parentSku: string, guaranteeSku: string) {

    if (isCartFrozen.value) {

      return;

    }

    const action: CartUpdateAction = addExtraGuaranteeLineItem(parentSku, guaranteeSku);
    await updateCustomCart([action]);

    handleCartUpdate({ action: CiaUpdateAction.Add, sku: parentSku });

  }

  async function replicateCart(parentOrderNumber: string) {

    await oldReplicateCartQueued(parentOrderNumber);

    await load();

  }

  async function setExtraGuaranteeProductToCart(
    params: { parentSku: string, guaranteeSku: string, parentOrderNumber: string }) {

    if (isCartFrozen.value) {

      return;

    }

    await replicateCart(params.parentOrderNumber);

    if (!cart.value?.id || !cart.value?.version) {

      throw new Error('Cart not initialized after replicateCart');

    }

    const addExtraGuaranteeAction: CartUpdateAction =
      addExtraGuaranteeLineItem(params.parentSku, params.guaranteeSku);
    const actions = [addExtraGuaranteeAction];

    await oldUpdateCartQueued({
      id: cart?.value?.id,
      version: cart?.value?.version,
      actions,
    }, UPDATE_CART_QUERY);

    throwIfUpdateCartFailed();

    handleCartUpdate({ action: CiaUpdateAction.Add, sku: params.guaranteeSku });

  }

  function getSetCartCustomFieldsActions(fields: CustomFieldInput[]): CartUpdateAction[] {

    return cart.value?.custom
      ? setCustomFieldsAction(fields)
      : [setCustomTypeAction(fields)];

  }

  async function setCartCustomFields(fields: CustomFieldInput[]) {

    await updateCustomCart(getSetCartCustomFieldsActions(fields));

  }

  async function attachReviewConsentAndUtmSource(reviewConsent: boolean) {

    await setCartCustomFields([
      {
        name: 'customerAgreedToSendEmail',
        value: String(reviewConsent),
      },
      {
        name: 'utmSource',
        value: JSON.stringify(utmSourceCookie.value ?? ''),
      },
    ]);

  }

  const setUpdateCustomCartError = (value: string) => {

    customError.value.updateCustomCart = value;

  };

  const throwIfUpdateCartFailed = () => {

    const updateCartError = oldError.value.updateCart;

    if (!updateCartError) {

      return;

    }

    throw new Error(updateCartError);

  };

  const _updateCustomCartWithRetries = async (actions: CartUpdateAction[]) => {

    setUpdateCustomCartError('');
    const { error } = await callFunctionWithRetries(() => updateCustomCartFunction(actions));
    setUpdateCustomCartError(error);

  };
  const _updateCustomCartDebounced = asyncDebounce(async (actions: CartUpdateAction[]) => {

    actionsSaved.value = [];
    await _updateCustomCartWithRetries(actions);

  }, DEBOUNCE_TIMES.CART_API);
  const actionsSaved = useState<CartUpdateAction[]>('actionsCart', () => []);

  async function updateCustomCart(actions: CartUpdateAction[]) {

    actionsSaved.value.push(...actions);
    const currentActionsSaved = [...actionsSaved.value];
    await _updateCustomCartDebounced(currentActionsSaved);

  }
  const updateCustomCartFunction = async (actions: CartUpdateAction[]) => {

    if (!actions.length || !cart.value) {

      return;

    }

    if (isCartFrozen.value && actions.some(isForbiddenCartUpdateAction)) {

      Logger.warn(`[updateCustomCart] Skipping cart update because cartState is Frozen: ${cart.value.cartState}`);

      return;

    }

    await oldUpdateCartQueued({
      id: cart.value.id,
      version: cart.value.version,
      actions,
    }, UPDATE_CART_QUERY);
    throwIfUpdateCartFailed();

  };

  async function recalculateCart() {

    if (isCartFrozen.value) {

      return;

    }

    await updateCustomCart([
      {
        recalculate: {
          updateProductData: true,
        },
      },
    ]);

  }

  async function setShippingDetails(address: Partial<Address>) {

    if (cart.value) {

      let actions: CartUpdateAction[] = [];
      actions = [
        setShippingAddressAction(address),
      ];
      await updateCustomCart(actions);

    }

  }

  async function setBillingDetails(
    address: Partial<Address>,
    {
      vatNumber,
      vatResult = null,
      shippingAddress,
    }: {
      vatNumber: VatNumber;
      vatResult?: VatValidationResult | null;
      shippingAddress?: Partial<Address>;
    },
  ) {

    if (!cart.value) {

      return;

    }

    // The address is replaced whole, so its custom fields go with it and have to be restated here.
    // Passing neither a number nor a result states the empty set, which is the intent of a caller
    // that has no VAT details to put on the address.
    const actions: CartUpdateAction[] = [
      setBillingAddressAction(address),
      setAddressCustomFieldsAction({
        addressType: 'Billing',
        vatNumber,
        vatResult,
        selfDeclaration: getSelfDeclaration(billingClientType.value),
      }),
    ];

    if (shippingAddress) {

      actions.push(setShippingAddressAction(shippingAddress));

    }

    await updateCustomCart(actions);

  }

  async function load() {

    const previousCartId = cart.value?.id;
    await oldLoad({});
    isLoaded.value = true;

    if (!cart.value) {

      return;

    }

    if (!previousCartId && cart.value.id) {

      logCreatedCartId(cart.value.id);

    }
    await clearIfOtherStoreCart();

    if (!cart.value) {

      return;

    }
    persistCartId(cart.value.id);

    if (cart.value.lineItems) {

      loadStock({ items: cart.value.lineItems });

    }

  }

  async function clearIfOtherStoreCart() {

    const storeKey = getStoreKey(cart.value);

    if (storeKey && (storeKey !== countryLocalization?.value.storeId)) {

      await clear();

    }

  }

  const addItem = (params:
    { product: ProductVariant, quantity: number, triggeredIn?: EventSourceLocation },
  ) => callFunctionWithRetries(async () => {

    await load();

    if (isCartFrozen.value) {

      return;

    }

    const lineItem = cart.value?.lineItems?.find((item: LineItem) => item.variant?.sku === params.product.sku);

    if (lineItem) {

      const newQuantity = lineItem.quantity + params.quantity;
      await updateItemQty({
        product: lineItem,
        quantity: newQuantity,
        customQuery: UPDATE_CART_QUERY,
      }, CiaUpdateAction.Add);

      return;

    }
    const previousCartId = cart.value?.id;

    await oldAddItemQueued({
      ...params,
      customQuery: UPDATE_CART_QUERY,
    });

    if (!previousCartId && cart.value?.id) {

      logCreatedCartId(cart.value.id);
      persistCartId(cart.value.id);

    }

    const message = error.value.addItem;

    if (!message) {

      await updateCustomerEmail(userEmailAddress.value, 'addItem');
      handleCartUpdate({ action: CiaUpdateAction.Add, sku: params.product.sku });

      if (cart?.value?.lineItems) {

        loadStock({ items: cart.value.lineItems, cartId: cart.value.id });
      
      }

      return;

    }
    await handleError(message);

  });

  const removeItem = (params: { product: LineItem }) => callFunctionWithRetries(async () => {

    if (!cart.value || isCartFrozen.value) {

      return;

    }
    persistCartId(cart.value.id);
    await oldRemoveItemQueued({ ...params, customQuery: UPDATE_CART_QUERY });
    const message = error.value.removeItem;

    if (!message) {

      handleCartUpdate({
        action: CiaUpdateAction.Remove,
        sku: params.product?.variant?.sku,
      });

      return;

    }
    await handleError(message);

  });

  async function updateItemQty(params: {
    product: LineItem,
    quantity?: number,
    customQuery?: CustomQuery
  }, ciaActionType = CiaUpdateAction.Add) {

    if (!cart.value || isCartFrozen.value) {

      return;

    } // TODO - validate if this if makes sense for this check  INSP-776

    if (!params.quantity) {

      await removeItem(params);

      return;

    }
    await oldUpdateItemQtyQueued(params);
    const message = error.value.updateItemQty;

    if (!message) {

      handleCartUpdate({ action: ciaActionType, sku: params.product?.variant?.sku });

      return;

    }
    await handleError(message);

  }

  async function handleError(message: string) {

    if (message?.includes(ERROR_NOT_FOUND_IN_STORE)) {

      await clear();

      return;

    }
    await reloadCart();
    throw new Error(message);

  }

  async function clear() {

    if (cart.value) { // TODO - validate if this if makes sense for this check  INSP-776

      persistCartId('');
      await oldClearQueued();
      setCart(null);

    }

  }

  async function applyCoupon(params: { couponCode: string, customQuery?: CustomQuery }) {

    if (cart.value && !isCartFrozen.value) { // TODO - validate if this if makes sense for this check  INSP-776

      persistCartId(cart.value.id);
      await oldApplyCouponQueued(params);

    }

  }

  async function removeCoupon(params: { couponCode: string, customQuery?: CustomQuery }) {

    if (cart.value && !isCartFrozen.value) { // TODO - validate if this if makes sense for this check  INSP-776

      persistCartId(cart.value.id);
      await oldRemoveCouponQueued(params);

    }

  }

  const updateQuantity = (product: LineItem, quantity: number) => callFunctionWithRetries(() =>
    updateItemQty({
      product,
      quantity,
      customQuery: UPDATE_CART_QUERY,
    }),
  );

  const taxMode = computed(() => cart?.value?.taxMode);

  /**
   * The tax rate of an external tax mode cart is applied on top of the price of a line item, so
   * that price is a net amount while the one the customer pays is gross. Anything that compares
   * the two - a strikethrough reference against the final price above all - has to account for it.
   */
  const isExternalTaxMode = computed(() => taxMode.value === TaxMode.External);

  const calculatedCodFee = computed(() =>
    cart?.value?.custom?.customFieldsRaw?.find(field => field.name === 'calculatedCodFee')?.value);

  const codFeeAmount = computed(() =>
    calculatedCodFee.value ? calculatedCodFee.value.centAmount / (10 ** calculatedCodFee.value.fractionDigits) : 0);

  const isExtraGuaranteeOnlyCart = computed(() => {

    return cart.value ? getIsExtraGuaranteeOnlyCart(cart.value) : false;

  });

  return {
    // TODO - revert syntax once fix is pushed for type incoherence between composables and getters -
    // https://expondo.atlassian.net/browse/INSP-988
    cart: cart as Ref<Cart>,
    codFeeAmount,
    loading,
    isLoaded: computed(() => (isLoaded.value || !!cart?.value?.version)),
    error,
    setCart,
    updateCustomerEmail,
    updatePayment,
    setShippingDetails,
    setBillingDetails,
    updateCustomCart,
    load,
    clearIfOtherStoreCart,
    addItem,
    removeItem,
    updateItemQty,
    applyCoupon,
    removeCoupon,
    reloadCart,
    setUpdateCustomCartError,
    updateQuantity,
    taxMode,
    isExternalTaxMode,
    addExtraGuaranteeProduct,
    attachReviewConsentAndUtmSource,
    setCartCustomFields,
    recalculateCart,
    setExtraGuaranteeProductToCart,
    isExtraGuaranteeOnlyCart,
    isCartActive,
    isCartFrozen,
  };

}
